<template>
  <div class="prospects-manager">
    <!-- Filters -->
    <div class="filters-section">
      <div class="search-box">
        <input
          v-model="filters.search"
          type="text"
          placeholder="Rechercher par nom, téléphone ou description..."
          class="search-input"
        />
      </div>
      
      <div class="filter-controls">
        <!-- Vue Toggle -->
        <div class="view-toggle">
          <button 
            @click="viewMode = 'table'" 
            :class="['view-btn', { active: viewMode === 'table' }]"
            title="Vue tableau"
          >
            <i class="fas fa-table"></i>
          </button>
          <button 
            @click="viewMode = 'cards'" 
            :class="['view-btn', { active: viewMode === 'cards' }]"
            title="Vue cartes"
          >
            <i class="fas fa-th-large"></i>
          </button>
        </div>

        <!-- Statut Filter -->
        <div class="filter-wrapper" :class="getStatutClass">
          <div class="filter-icon">
            <i class="fas fa-flag-checkered"></i>
          </div>
          <select v-model="filters.statut" @change="applyFilters" class="filter-select premium-select">
            <option value="">Tous les statuts</option>
            <option value="Reporté">🔄 Reporté</option>
            <option value="Planifié">📅 Planifié</option>
            <option value="Rejeté">❌ Rejeté</option>
            <option value="Terminé">✔️ Terminé</option>
            <option value="Annulé">🚫 Annulé</option>
          </select>
          <div class="filter-arrow">
            <i class="fas fa-chevron-down"></i>
          </div>
          <div v-if="filters.statut" class="selection-indicator">
            <i class="fas fa-check-circle"></i>
            <span class="selection-badge">1</span>
          </div>
        </div>

        <!-- Type Intervention Filter -->
        <div class="filter-wrapper" :class="getTypeClass">
          <div class="filter-icon">
            <i class="fas fa-tools"></i>
          </div>
          <select v-model="filters.typeIntervention" @change="applyFilters" class="filter-select premium-select">
            <option value="">Tous les types</option>
            <option value="Installation">🔧 Installation</option>
            <option value="Intervention">⚙️ Intervention</option>
            <option value="Prospect">💼 Prospect</option>
          </select>
          <div class="filter-arrow">
            <i class="fas fa-chevron-down"></i>
          </div>
          <div v-if="filters.typeIntervention" class="selection-indicator">
            <i class="fas fa-check-circle"></i>
            <span class="selection-badge">1</span>
          </div>
        </div>

        <!-- Commercial Filter -->
        <div class="filter-wrapper" :class="getCommercialClass">
          <div class="filter-icon">
            <i class="fas fa-user-tie"></i>
          </div>
          <select v-model="filters.commercial" @change="applyFilters" class="filter-select premium-select">
            <option value="">Tous les commerciaux</option>
            <option v-for="commercial in commerciaux" :key="commercial._id" :value="commercial._id">
              {{ commercial.prenom }} {{ commercial.nom }}
            </option>
          </select>
          <div class="filter-arrow">
            <i class="fas fa-chevron-down"></i>
          </div>
          <div v-if="filters.commercial" class="selection-indicator">
            <i class="fas fa-check-circle"></i>
            <span class="selection-badge">1</span>
          </div>
        </div>

        <!-- Date Filter -->
        <div class="filter-wrapper date-filter-wrapper">
          <div class="filter-icon">
            <i class="fas fa-calendar"></i>
          </div>
          <input 
            type="date" 
            v-model="filters.dateCreation" 
            @change="applyFilters" 
            class="filter-date"
            placeholder="Date du RDV"
            title="Filtrer par date du rendez-vous"
          />
        </div>

        <button 
          @click="resetFilters"
          class="btn btn-secondary reset-btn"
          :disabled="!hasActiveFilters"
        >
          <i class="fas fa-undo"></i>
          Réinitialiser
        </button>
      </div>
    </div>

    <!-- Content Container -->
    <div class="prospects-content-container">
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
        <p>Chargement des prospects...</p>
      </div>

      <div v-else-if="prospects.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-users"></i>
        </div>
        <h3>Aucun prospect trouvé</h3>
        <p v-if="hasActiveFilters">Aucun prospect ne correspond à vos critères de recherche.</p>
        <p v-else>Aucun prospect n'a été créé pour le moment.</p>
        <button v-if="hasActiveFilters" @click="resetFilters" class="btn btn-primary">
          <i class="fas fa-undo"></i>
          Réinitialiser les filtres
        </button>
      </div>

      <!-- Vue Tableau -->
      <div v-else-if="viewMode === 'table'" class="table-view">
        <table class="table prospects-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Contact</th>
              <th>Commercial</th>
              <th>Type</th>
              <!-- Colonnes dynamiques pour les catégories -->
              <th v-for="category in allCategories" :key="category._id" class="category-column">
                <div class="category-header">
                  <span class="category-emoji">{{ category.emoji }}</span>
                  <span class="category-name">{{ category.nom }}</span>
                </div>
              </th>
              <th>Date RDV</th>
              <th>Lieu</th>
              <th>Prix</th>
              <th>Priorité</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="prospect in prospects" :key="prospect._id" :class="getRowStatusClass(prospect.statut)">
              <td>
                <div class="client-info">
                  <strong>{{ prospect.client.nomComplet }}</strong>
                  <small v-if="prospect.client.email">{{ prospect.client.email }}</small>
                </div>
              </td>
              <td>{{ prospect.client.telephone }}</td>
              <td>
                <div class="commercial-info">
                  <i class="fas fa-user-tie"></i>
                  <span v-if="prospect.commercial && (prospect.commercial.nom || prospect.commercial.prenom)">
                    {{ prospect.commercial.prenom }} {{ prospect.commercial.nom }}
                  </span>
                  <span v-else class="no-commercial">Non assigné</span>
                </div>
              </td>
              <td>
                <span class="type-badge">
                  {{ getTypeIcon(prospect.typeIntervention) }} {{ prospect.typeIntervention }}
                </span>
              </td>
              <!-- Colonnes dynamiques pour les quantités par catégorie -->
              <td v-for="category in allCategories" :key="category._id" class="category-cell">
                <span v-if="getCategoryQuantity(prospect, category._id)" class="category-qty-badge">
                  {{ getCategoryQuantity(prospect, category._id) }}
                </span>
                <span v-else class="no-qty">-</span>
              </td>
              <td>
                <span v-if="getProchainRdv(prospect)" class="date-rdv">
                  <i class="fas fa-calendar"></i>
                  {{ formatDateRdv(getProchainRdv(prospect)) }}
                </span>
                <span v-else class="no-rdv">
                  <i class="fas fa-calendar-times"></i>
                  Non planifié
                </span>
              </td>
              <td>
                <span v-if="prospect.client.adresse?.ville" class="lieu">
                  <i class="fas fa-map-marker-alt"></i>
                  {{ prospect.client.adresse.ville }}
                </span>
                <span v-else class="no-lieu">-</span>
              </td>
              <td>
                <span v-if="prospect.prix" class="prix">
                  <i class="fas fa-euro-sign"></i>
                  {{ formatPrix(prospect.prix) }}
                </span>
                <span v-else class="no-prix">-</span>
              </td>
              <td>
                <span :class="getPrioriteBadgeClass(prospect.priorite)">
                  {{ getPrioriteIcon(prospect.priorite) }} {{ prospect.priorite }}
                </span>
              </td>
              <td>
                <div class="table-actions">
                  <!-- Boutons pour prospects Reportés -->
                  <button 
                    v-if="prospect.statut === 'Reporté'"
                    @click="validateProspect(prospect)" 
                    class="btn btn-sm btn-success"
                    title="Valider et replanifier"
                  >
                    <i class="fas fa-check"></i>
                  </button>
                  <button 
                    v-if="prospect.statut === 'Reporté'"
                    @click="rejectProspect(prospect)" 
                    class="btn btn-sm btn-danger"
                    title="Rejeter"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                  
                  <!-- Boutons standards -->
                  <button 
                    v-if="prospect.statut !== 'Terminé' && prospect.statut !== 'Reporté'"
                    @click="editProspect(prospect)" 
                    class="btn btn-sm btn-warning"
                    title="Modifier"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button 
                    v-if="prospect.statut === 'En attente'"
                    @click="cancelProspect(prospect)" 
                    class="btn btn-sm btn-danger"
                    title="Annuler"
                  >
                    <i class="fas fa-times-circle"></i>
                  </button>
                  <button 
                    @click="selectProspect(prospect)" 
                    class="btn btn-sm btn-primary"
                    title="Détails"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Vue Cartes -->
      <div v-else class="cards-view">
        <div class="prospects-grid">
          <div 
            v-for="prospect in prospects" 
            :key="prospect._id" 
            :class="['prospect-card', getCardStatusClass(prospect.statut)]"
            @click="selectProspect(prospect)"
          >
            <div class="card-header">
              <div class="client-name">
                <i class="fas fa-user"></i>
                <strong>{{ prospect.client.nomComplet }}</strong>
              </div>
              <span :class="getStatutBadgeClass(prospect.statut)">
                {{ getStatutIcon(prospect.statut) }} {{ prospect.statut }}
              </span>
            </div>
            
            <div class="card-body">
              <div class="contact-info">
                <div class="contact-item">
                  <i class="fas fa-phone"></i>
                  <span>{{ prospect.client.telephone }}</span>
                </div>
                <div v-if="prospect.client.email" class="contact-item">
                  <i class="fas fa-envelope"></i>
                  <span>{{ prospect.client.email }}</span>
                </div>
                <div v-if="prospect.commercial" class="contact-item commercial-item">
                  <i class="fas fa-user-tie"></i>
                  <span class="commercial-name">{{ prospect.commercial.prenom }} {{ prospect.commercial.nom }}</span>
                </div>
                <div v-if="prospect.technicienAffecte" class="contact-item technicien-item">
                  <i class="fas fa-wrench"></i>
                  <span class="technicien-name">{{ prospect.technicienAffecte.prenom }} {{ prospect.technicienAffecte.nom }}</span>
                </div>
                <div v-if="prospect.client.adresse?.rue" class="contact-item">
                  <i class="fas fa-map-marker-alt"></i>
                  <span>{{ prospect.client.adresse.rue }}, {{ prospect.client.adresse.ville }}</span>
                </div>
              </div>
              
              <div class="intervention-info">
                <div class="info-item">
                  <span class="type-badge">
                    {{ getTypeIcon(prospect.typeIntervention) }} {{ prospect.typeIntervention }}
                  </span>
                </div>
                <div class="info-item">
                  <span :class="getPrioriteBadgeClass(prospect.priorite)">
                    {{ getPrioriteIcon(prospect.priorite) }} {{ prospect.priorite }}
                  </span>
                </div>
                <div v-if="prospect.nombreAppareils" class="info-item">
                  <i class="fas fa-microchip"></i>
                  <span>{{ prospect.nombreAppareils }} appareil(s)</span>
                </div>
              </div>
              
              <div v-if="prospect.description" class="description">
                <p>{{ prospect.description }}</p>
              </div>
            </div>
            
            <div class="card-footer">
              <div class="creation-date">
                <i class="fas fa-calendar"></i>
                <small>{{ formatDate(prospect.dateCreation) }}</small>
              </div>
              <div class="action-buttons" @click.stop>
                <!-- Boutons pour prospects Reportés -->
                <button 
                  v-if="prospect.statut === 'Reporté'"
                  @click="validateProspect(prospect)" 
                  class="btn btn-sm btn-success"
                  title="Valider et replanifier"
                >
                  <i class="fas fa-check"></i>
                  Valider
                </button>
                <button 
                  v-if="prospect.statut === 'Reporté'"
                  @click="rejectProspect(prospect)" 
                  class="btn btn-sm btn-danger"
                  title="Rejeter"
                >
                  <i class="fas fa-times"></i>
                  Rejeter
                </button>
                
                <!-- Boutons standards -->
                <button 
                  v-if="prospect.statut !== 'Terminé' && prospect.statut !== 'Reporté'"
                  @click="editProspect(prospect)" 
                  class="btn btn-sm btn-warning"
                  title="Modifier le prospect"
                >
                  <i class="fas fa-edit"></i>
                  Modifier
                </button>
                <button 
                  v-if="prospect.statut === 'En attente'"
                  @click="cancelProspect(prospect)" 
                  class="btn btn-sm btn-danger"
                  title="Annuler le prospect"
                >
                  <i class="fas fa-times-circle"></i>
                  Annuler
                </button>
                <button 
                  @click="selectProspect(prospect)" 
                  class="btn btn-sm btn-primary select-btn"
                  title="Voir les détails"
                >
                  <i class="fas fa-eye"></i>
                  Détails
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="prospects.length > 0" class="pagination-container">
        <div class="pagination-info">
          <span class="pagination-text">
            Affichage de <strong>{{ (currentPage - 1) * itemsPerPage + 1 }}</strong> à 
            <strong>{{ Math.min(currentPage * itemsPerPage, totalProspects) }}</strong> sur 
            <strong>{{ totalProspects }}</strong> prospects
          </span>
          <div class="items-per-page">
            <label>Afficher:</label>
            <select v-model="itemsPerPage" @change="changeItemsPerPage(itemsPerPage)" class="per-page-select">
              <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
            <span>par page</span>
          </div>
        </div>
        
        <div class="pagination-controls">
          <button 
            class="pagination-btn pagination-nav"
            :disabled="currentPage === 1"
            @click="goToPage(1)"
            title="Première page"
          >
            <i class="fas fa-angle-double-left"></i>
          </button>
          <button 
            class="pagination-btn pagination-nav"
            :disabled="currentPage === 1"
            @click="prevPage"
            title="Page précédente"
          >
            <i class="fas fa-angle-left"></i>
          </button>
          
          <template v-for="page in visiblePages" :key="page">
            <span v-if="page === '...'" class="pagination-ellipsis">...</span>
            <button 
              v-else
              :class="['pagination-btn', { active: page === currentPage }]"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </template>
          
          <button 
            class="pagination-btn pagination-nav"
            :disabled="currentPage === totalPages"
            @click="nextPage"
            title="Page suivante"
          >
            <i class="fas fa-angle-right"></i>
          </button>
          <button 
            class="pagination-btn pagination-nav"
            :disabled="currentPage === totalPages"
            @click="goToPage(totalPages)"
            title="Dernière page"
          >
            <i class="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de visualisation (lecture seule) -->
    <ProspectViewModal
      v-if="showViewModal && selectedProspect"
      :prospect="selectedProspect"
      :user-role="userRole"
      @close="closeViewModal"
      @edit="openEditModal"
    />

    <!-- Modal d'édition (formulaire) -->
    <ProspectDetailModal
      v-if="showEditModal && selectedProspect"
      :prospect="selectedProspect"
      :user-role="userRole"
      @close="closeEditModal"
      @prospect-updated="handleProspectUpdated"
    />

    <!-- Modal d'affectation de technicien (pour prospects reportés) -->
    <AffectationTechnicienModal
      v-if="showAffectationModal && selectedProspect"
      :prospect="selectedProspect"
      @close="closeAffectationModal"
      @confirm="confirmAffectation"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useToast } from 'vue-toastification'
import ProspectDetailModal from './ProspectDetailModal.vue'
import ProspectViewModal from './ProspectViewModal.vue'
import AffectationTechnicienModal from './AffectationTechnicienModal.vue'
import categoryService from '@/services/categoryService'

export default {
  name: 'ProspectsManager',
  components: {
    ProspectDetailModal,
    ProspectViewModal,
    AffectationTechnicienModal
  },
  props: ['userRole'],
  emits: ['prospect-selected', 'prospects-count'],
  setup(props, { emit }) {
    const toast = useToast()
    const prospects = ref([])
    const allCategories = ref([])
    const loading = ref(false)
    const selectedProspect = ref(null)
    const showViewModal = ref(false)
    const showEditModal = ref(false)
    const showAffectationModal = ref(false)
    const viewMode = ref('table')
    const isMobile = ref(window.innerWidth <= 768)

    // Détecter le mode mobile et forcer les cartes
    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768
      if (isMobile.value) {
        viewMode.value = 'cards'
      }
    }

    const filters = ref({
      search: '',
      statut: '',
      typeIntervention: '',
      commercial: '',
      dateCreation: ''
    })

    const commerciaux = ref([])

    // Charger la liste des commerciaux
    const loadCommerciaux = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/users?role=Commercial&limit=100', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        if (response.ok) {
          const data = await response.json()
          // Gérer les deux formats de réponse possibles
          commerciaux.value = data.utilisateurs || data.users || data || []
          console.log('Commerciaux chargés:', commerciaux.value.length)
        }
      } catch (error) {
        console.error('Erreur lors du chargement des commerciaux:', error)
      }
    }

    // Pagination
    const currentPage = ref(1)
    const totalPages = ref(1)
    const totalProspects = ref(0)
    const itemsPerPage = ref(10)
    const itemsPerPageOptions = [5, 10, 20, 50]

    const loadCategories = async () => {
      try {
        const response = await categoryService.getCategories(true)
        // Filtrer uniquement les catégories à afficher dans les prospects
        const allCats = response.data.data || []
        allCategories.value = allCats.filter(cat => 
          cat.afficherDansProspects === true || cat.afficherDansProspects === undefined
        )
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error)
        allCategories.value = []
      }
    }

    const getCategoryQuantity = (prospect, categoryId) => {
      // Vérifier d'abord dans le tableau categories (nouveau format)
      if (prospect.categories && prospect.categories.length > 0) {
        const category = prospect.categories.find(cat => 
          cat.categorieId === categoryId || cat.categorieId?._id === categoryId
        )
        if (category) return category.quantite
      }
      
      // Sinon vérifier dans le tableau appareils (format de compatibilité)
      if (prospect.appareils && prospect.appareils.length > 0) {
        // Trouver le nom de la catégorie correspondant à categoryId
        const categoryObj = allCategories.value.find(cat => cat._id === categoryId)
        if (categoryObj) {
          const appareil = prospect.appareils.find(app => app.type === categoryObj.nom)
          if (appareil) return appareil.quantite
        }
      }
      
      return 0
    }

    const loadProspects = async () => {
      loading.value = true
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          throw new Error('Token d\'authentification manquant')
        }

        // Construction des paramètres de requête
        const params = new URLSearchParams()
        if (filters.value.search) params.append('search', filters.value.search)
        if (filters.value.statut) params.append('statut', filters.value.statut)
        if (filters.value.typeIntervention) params.append('typeIntervention', filters.value.typeIntervention)
        if (filters.value.commercial) params.append('commercial', filters.value.commercial)
        if (filters.value.dateCreation) params.append('dateCreation', filters.value.dateCreation)
        params.append('page', currentPage.value.toString())
        params.append('limit', itemsPerPage.value.toString())

        const response = await fetch(`/api/prospects?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`)
        }

        const data = await response.json()
        prospects.value = data.prospects || []
        
        // Mise à jour des infos de pagination (compatible avec le backend)
        if (data.pagination) {
          totalProspects.value = data.pagination.totalItems || 0
          totalPages.value = data.pagination.totalPages || 1
          currentPage.value = data.pagination.currentPage || 1
        } else {
          totalProspects.value = data.total || data.prospects?.length || 0
          totalPages.value = data.totalPages || Math.ceil(totalProspects.value / itemsPerPage.value) || 1
        }
        
        emit('prospects-count', totalProspects.value)
      } catch (error) {
        console.error('Erreur lors du chargement des prospects:', error)
        prospects.value = []
        totalProspects.value = 0
        totalPages.value = 1
        emit('prospects-count', 0)
      } finally {
        loading.value = false
      }
    }

    // Fonctions de pagination
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page
        loadProspects()
      }
    }

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++
        loadProspects()
      }
    }

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
        loadProspects()
      }
    }

    const changeItemsPerPage = (newLimit) => {
      itemsPerPage.value = newLimit
      currentPage.value = 1 // Reset to first page
      loadProspects()
    }

    // Pages visibles pour la pagination
    const visiblePages = computed(() => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        if (current <= 3) {
          for (let i = 1; i <= 5; i++) pages.push(i)
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 2) {
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) pages.push(i)
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) pages.push(i)
          pages.push('...')
          pages.push(total)
        }
      }
      return pages
    })

    const hasActiveFilters = computed(() => {
      return filters.value.search || filters.value.statut || filters.value.typeIntervention || filters.value.commercial || filters.value.dateCreation
    })

    const getStatutClass = computed(() => filters.value.statut ? 'has-selection' : '')
    const getTypeClass = computed(() => filters.value.typeIntervention ? 'has-selection' : '')
    const getCommercialClass = computed(() => filters.value.commercial ? 'has-selection' : '')

    const applyFilters = () => {
      loadProspects()
    }

    const resetFilters = () => {
      filters.value = {
        search: '',
        statut: '',
        typeIntervention: '',
        commercial: '',
        dateCreation: ''
      }
      currentPage.value = 1 // Reset pagination
      applyFilters()
    }

    const selectProspect = (prospect) => {
      selectedProspect.value = prospect
      showViewModal.value = true
      emit('prospect-selected', prospect)
    }

    const closeViewModal = () => {
      showViewModal.value = false
      selectedProspect.value = null
    }

    const openEditModal = (prospect) => {
      showViewModal.value = false
      selectedProspect.value = prospect || selectedProspect.value
      showEditModal.value = true
    }

    const closeEditModal = () => {
      showEditModal.value = false
      selectedProspect.value = null
    }

    const handleProspectUpdated = (updatedProspect) => {
      // Mettre à jour le prospect dans la liste
      const index = prospects.value.findIndex(p => p._id === updatedProspect._id)
      if (index !== -1) {
        prospects.value[index] = updatedProspect
      }
      closeEditModal()
      // Recharger la liste pour être sûr
      loadProspects()
    }

    const editProspect = (prospect) => {
      // Ouvrir le modal d'édition directement
      selectedProspect.value = prospect
      showEditModal.value = true
    }

    const cancelProspect = async (prospect) => {
      if (!confirm(`Êtes-vous sûr de vouloir annuler le prospect "${prospect.client.nomComplet}" ?`)) {
        return
      }

      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`/api/prospects/${prospect._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...prospect,
            statut: 'Annulé'
          })
        })

        if (response.ok) {
          toast.success('✅ Prospect annulé avec succès')
          loadProspects()
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de l\'annulation')
        }
      } catch (error) {
        console.error('Erreur lors de l\'annulation du prospect:', error)
        toast.error('Erreur de connexion au serveur')
      }
    }

    const validateProspect = async (prospect) => {
      // Ouvrir le modal d'affectation pour replanifier
      selectedProspect.value = prospect
      showAffectationModal.value = true
    }

    const closeAffectationModal = () => {
      showAffectationModal.value = false
      selectedProspect.value = null
    }

    const confirmAffectation = async (data) => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`/api/prospects/${selectedProspect.value._id}/valider`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(data)
        })

        if (response.ok) {
          toast.success('✅ Prospect replanifié avec succès')
          closeAffectationModal()
          loadProspects()
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de la replanification')
        }
      } catch (error) {
        console.error('Erreur lors de la replanification du prospect:', error)
        toast.error('Erreur de connexion au serveur')
      }
    }

    const rejectProspect = async (prospect) => {
      const motif = prompt(`Motif du rejet du prospect "${prospect.client.nomComplet}" :`)
      if (!motif) {
        return
      }

      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`/api/prospects/${prospect._id}/rejeter`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ motif })
        })

        if (response.ok) {
          toast.success('✅ Prospect rejeté avec succès')
          loadProspects()
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors du rejet')
        }
      } catch (error) {
        console.error('Erreur lors du rejet du prospect:', error)
        toast.error('Erreur de connexion au serveur')
      }
    }

    const getStatutBadgeClass = (statut) => {
      const classes = {
        'En attente': 'badge badge-warning',
        'Validé': 'badge badge-success',
        'Rejeté': 'badge badge-danger',
        'Reporté': 'badge badge-reported',
        'Planifié': 'badge badge-info',
        'Terminé': 'badge badge-completed',
        'Annulé': 'badge badge-secondary'
      }
      return classes[statut] || 'badge badge-secondary'
    }

    const getRowStatusClass = (statut) => {
      const classes = {
        'En attente': 'row-status-warning',
        'Validé': 'row-status-success',
        'Rejeté': 'row-status-danger',
        'Reporté': 'row-status-reported',
        'Planifié': 'row-status-info',
        'Terminé': 'row-status-completed',
        'Annulé': 'row-status-secondary'
      }
      return classes[statut] || ''
    }

    const getCardStatusClass = (statut) => {
      const classes = {
        'En attente': 'card-status-warning',
        'Validé': 'card-status-success',
        'Rejeté': 'card-status-danger',
        'Reporté': 'card-status-reported',
        'Planifié': 'card-status-info',
        'Terminé': 'card-status-completed',
        'Annulé': 'card-status-secondary'
      }
      return classes[statut] || ''
    }

    const getPrioriteBadgeClass = (priorite) => {
      const classes = {
        'Haute': 'badge badge-danger',
        'Moyenne': 'badge badge-warning',
        'Basse': 'badge badge-secondary'
      }
      return classes[priorite] || 'badge badge-secondary'
    }

    const getStatutIcon = (statut) => {
      const icons = {
        'En attente': '⏳',
        'Validé': '✅',
        'Rejeté': '❌',
        'Planifié': '📅',
        'Terminé': '✔️',
        'Terminé': '✔️',
        'Annulé': '🚫'
      }
      return icons[statut] || '📋'
    }

    const getPrioriteIcon = (priorite) => {
      const icons = {
        'Haute': '🔴',
        'Moyenne': '🟡',
        'Basse': '🟢'
      }
      return icons[priorite] || '⚪'
    }

    const getTypeIcon = (type) => {
      const icons = {
        'Installation': '🔧',
        'Intervention': '⚙️',
        'Prospect': '💼'
      }
      return icons[type] || '🛠️'
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    // Debounced search
    let searchTimeout = null
    const debouncedSearch = () => {
      if (searchTimeout) clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        loadProspects()
      }, 500)
    }

    // Watch for search changes
    watch(() => filters.value.search, debouncedSearch)

    // Fonctions utilitaires pour les nouvelles colonnes
    const getProchainRdv = (prospect) => {
      // Priorité 1 : RDV existant (tous statuts - Planifié, Terminé, etc.)
      if (prospect.rdvExistant) {
        return {
          dateSortie: prospect.rdvExistant.dateRdv, // dateRdv du RDV = date de sortie
          heureDebut: prospect.rdvExistant.heureDebut,
          heureFin: prospect.rdvExistant.heureFin
        }
      }
      
      // Priorité 2 : Dates proposées (toutes les dates, pas seulement futures)
      if (!prospect.datesProposes || prospect.datesProposes.length === 0) return null
      
      // Retourner la première date proposée avec dateSortie
      const premiereDatePropose = prospect.datesProposes[0]
      return {
        dateSortie: premiereDatePropose.dateSortie || premiereDatePropose.date,
        heureDebut: premiereDatePropose.heureDebut,
        heureFin: premiereDatePropose.heureFin
      }
    }

    const formatDateRdv = (dateRdv) => {
      if (!dateRdv) return ''
      // Utiliser dateSortie (date réelle du RDV) au lieu de date (date de création)
      const dateValue = dateRdv.dateSortie || dateRdv.date
      if (!dateValue) return ''
      const date = new Date(dateValue)
      const dateStr = date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      // Afficher date + heure début + heure fin
      const heureDebut = dateRdv.heureDebut || ''
      const heureFin = dateRdv.heureFin || ''
      if (heureDebut && heureFin) {
        return `${dateStr}\n${heureDebut}`
      } else if (heureDebut) {
        return `${dateStr}\n${heureDebut}`
      }
      return dateStr
    }

    const formatPrix = (prix) => {
      if (!prix) return '0'
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND',
        minimumFractionDigits: 2
      }).format(prix)
    }

    onMounted(() => {
      loadCategories()
      loadProspects()
      loadCommerciaux()
      // Initialiser le mode responsive
      handleResize()
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      prospects,
      allCategories,
      loading,
      selectedProspect,
      showViewModal,
      showEditModal,
      showAffectationModal,
      viewMode,
      filters,
      hasActiveFilters,
      getStatutClass,
      getTypeClass,
      getCommercialClass,
      commerciaux,
      loadProspects,
      applyFilters,
      resetFilters,
      selectProspect,
      closeViewModal,
      openEditModal,
      closeEditModal,
      handleProspectUpdated,
      editProspect,
      cancelProspect,
      validateProspect,
      rejectProspect,
      closeAffectationModal,
      confirmAffectation,
      getStatutBadgeClass,
      getPrioriteBadgeClass,
      getStatutIcon,
      getPrioriteIcon,
      getTypeIcon,
      getRowStatusClass,
      getCardStatusClass,
      userRole: props.userRole,
      formatDate,
      getProchainRdv,
      formatDateRdv,
      formatPrix,
      getCategoryQuantity,
      isMobile,
      // Pagination
      currentPage,
      totalPages,
      totalProspects,
      itemsPerPage,
      itemsPerPageOptions,
      visiblePages,
      goToPage,
      nextPage,
      prevPage,
      changeItemsPerPage
    }
  }
}
</script>

<style scoped>
.prospects-manager {
  padding: 1rem;
}

.filters-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

.search-box {
  display: flex;
  align-items: center;
}

.search-input {
  width: 100%;
  max-width: 400px;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
  background: rgba(255, 255, 255, 0.95);
}

.filter-controls {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

/* Filtres avec design glassmorphisme */
.filter-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 200px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 16px;
  padding: 0.75rem 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.filter-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 27, 183, 0.3);
}

.filter-wrapper:focus-within {
  transform: translateY(-3px);
  border-color: var(--primary-blue);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.15);
}

.filter-wrapper.has-selection {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1), rgba(0, 70, 255, 0.05));
  border-color: rgba(0, 27, 183, 0.3);
}

.filter-wrapper.has-selection .filter-icon i {
  color: var(--secondary-blue);
  animation: pulse 2s infinite;
}

.filter-wrapper.has-selection .premium-select {
  font-weight: 700;
  color: var(--secondary-blue);
}

.filter-icon {
  display: flex;
  align-items: center;
  margin-right: 0.75rem;
  color: #6b7280;
  font-size: 1rem;
}

.premium-select {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  outline: none;
  appearance: none;
}

.premium-select option {
  background: white;
  color: #374151;
  padding: 0.5rem;
}

.filter-date {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  outline: none;
  padding: 0.25rem;
}

.date-filter-wrapper {
  min-width: 180px;
}

.filter-arrow {
  margin-left: 0.5rem;
  color: #6b7280;
  transition: transform 0.3s ease;
  pointer-events: none;
}

.filter-wrapper:focus-within .filter-arrow {
  transform: rotate(180deg);
}

.selection-indicator {
  position: absolute;
  right: -8px;
  top: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selection-indicator i {
  color: #10b981;
  font-size: 1.2rem;
  animation: pulse 2s infinite;
}

.selection-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: bounceIn 0.6s ease;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.reset-btn {
  background: linear-gradient(135deg, rgba(108, 117, 125, 0.1), rgba(134, 142, 150, 0.05));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(108, 117, 125, 0.3);
  color: #6c757d;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
}

.reset-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(108, 117, 125, 0.2);
  background: linear-gradient(135deg, rgba(108, 117, 125, 0.15), rgba(134, 142, 150, 0.1));
}

.reset-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prospects-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.prospects-table th {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.prospects-table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

/* ============================================
   STYLES DES LIGNES PAR STATUT - COULEURS SOLIDES
   ============================================ */

/* En attente - Orange */
.row-status-warning {
  background: linear-gradient(90deg, #fef3c7, #fef9c3) !important;
  border-left: 5px solid #f59e0b !important;
}

/* Validé - Vert émeraude */
.row-status-success {
  background: linear-gradient(90deg, #d1fae5, #ecfdf5) !important;
  border-left: 5px solid #10b981 !important;
}

/* Rejeté - Rouge */
.row-status-danger {
  background: linear-gradient(90deg, #fee2e2, #fef2f2) !important;
  border-left: 5px solid #ef4444 !important;
}

/* Reporté - Violet/Mauve */
.row-status-reported {
  background: linear-gradient(90deg, #e9d5ff, #f3e8ff) !important;
  border-left: 5px solid #a855f7 !important;
}

/* Planifié - Bleu */
.row-status-info {
  background: linear-gradient(90deg, #dbeafe, #eff6ff) !important;
  border-left: 5px solid #3b82f6 !important;
}

/* Terminé/Terminé - Vert vif */
.row-status-completed {
  background: linear-gradient(90deg, #bbf7d0, #dcfce7) !important;
  border-left: 5px solid #22c55e !important;
  box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.3);
}

/* Annulé - Gris */
.row-status-secondary {
  background: linear-gradient(90deg, #e5e7eb, #f3f4f6) !important;
  border-left: 5px solid #6b7280 !important;
  opacity: 0.9;
}

.prospects-table tr {
  transition: all 0.25s ease;
}

.prospects-table tr:hover {
  transform: translateX(3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* ============================================
   HOVER POUR LES LIGNES
   ============================================ */

.row-status-warning:hover {
  background: linear-gradient(90deg, #fde68a, #fef3c7) !important;
}

.row-status-success:hover {
  background: linear-gradient(90deg, #a7f3d0, #d1fae5) !important;
}

.row-status-danger:hover {
  background: linear-gradient(90deg, #fecaca, #fee2e2) !important;
}

.row-status-reported:hover {
  background: linear-gradient(90deg, #d8b4fe, #e9d5ff) !important;
}

.row-status-info:hover {
  background: linear-gradient(90deg, #bfdbfe, #dbeafe) !important;
}

.row-status-completed:hover {
  background: linear-gradient(90deg, #86efac, #bbf7d0) !important;
}

.row-status-secondary:hover {
  background: linear-gradient(90deg, #d1d5db, #e5e7eb) !important;
  opacity: 1;
}

/* ============================================
   STYLES DES CARTES PAR STATUT
   ============================================ */

/* En attente - Orange */
.card-status-warning {
  background: linear-gradient(135deg, #fef3c7, #fffbeb) !important;
  border: 2px solid #f59e0b !important;
  border-top: 4px solid #f59e0b !important;
}

/* Validé - Vert émeraude */
.card-status-success {
  background: linear-gradient(135deg, #d1fae5, #ecfdf5) !important;
  border: 2px solid #10b981 !important;
  border-top: 4px solid #10b981 !important;
}

/* Rejeté - Rouge */
.card-status-danger {
  background: linear-gradient(135deg, #fee2e2, #fef2f2) !important;
  border: 2px solid #ef4444 !important;
  border-top: 4px solid #ef4444 !important;
}

/* Reporté - Violet/Mauve */
.card-status-reported {
  background: linear-gradient(135deg, #e9d5ff, #f3e8ff) !important;
  border: 2px solid #a855f7 !important;
  border-top: 4px solid #a855f7 !important;
}

/* Planifié - Bleu */
.card-status-info {
  background: linear-gradient(135deg, #dbeafe, #eff6ff) !important;
  border: 2px solid #3b82f6 !important;
  border-top: 4px solid #3b82f6 !important;
}

/* Terminé/Terminé - Vert vif */
.card-status-completed {
  background: linear-gradient(135deg, #bbf7d0, #dcfce7) !important;
  border: 2px solid #22c55e !important;
  border-top: 4px solid #22c55e !important;
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.25);
}

/* Annulé - Gris */
.card-status-secondary {
  background: linear-gradient(135deg, #e5e7eb, #f3f4f6) !important;
  border: 2px solid #6b7280 !important;
  border-top: 4px solid #6b7280 !important;
  opacity: 0.9;
}

/* ============================================
   HOVER POUR LES CARTES
   ============================================ */

.card-status-warning:hover {
  background: linear-gradient(135deg, #fde68a, #fef3c7) !important;
  box-shadow: 0 8px 25px rgba(245, 158, 11, 0.3) !important;
  transform: translateY(-5px) !important;
}

.card-status-success:hover {
  background: linear-gradient(135deg, #a7f3d0, #d1fae5) !important;
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3) !important;
  transform: translateY(-5px) !important;
}

.card-status-danger:hover {
  background: linear-gradient(135deg, #fecaca, #fee2e2) !important;
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3) !important;
  transform: translateY(-5px) !important;
}

.card-status-reported:hover {
  background: linear-gradient(135deg, #d8b4fe, #e9d5ff) !important;
  box-shadow: 0 8px 25px rgba(168, 85, 247, 0.3) !important;
  transform: translateY(-5px) !important;
}

.card-status-info:hover {
  background: linear-gradient(135deg, #bfdbfe, #dbeafe) !important;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3) !important;
  transform: translateY(-5px) !important;
}

.card-status-completed:hover {
  background: linear-gradient(135deg, #86efac, #bbf7d0) !important;
  box-shadow: 0 8px 30px rgba(34, 197, 94, 0.4) !important;
  transform: translateY(-5px) !important;
}

.card-status-secondary:hover {
  background: linear-gradient(135deg, #d1d5db, #e5e7eb) !important;
  box-shadow: 0 8px 25px rgba(107, 114, 128, 0.3) !important;
  transform: translateY(-5px) !important;
  opacity: 1;
}

.loading-state {
  text-align: center;
  padding: 3rem;
  color: #6b7280;
}

.loading-spinner {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: var(--primary-blue);
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6b7280;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

.empty-state h3 {
  margin-bottom: 0.5rem;
  color: #374151;
}

.empty-state p {
  margin-bottom: 1.5rem;
  color: #6b7280;
}

.client-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.client-info small {
  color: #6b7280;
  font-size: 0.75rem;
}

.commercial-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.commercial-info i {
  color: var(--primary-blue);
  font-size: 0.75rem;
}

.commercial-info span {
  font-weight: 500;
  color: #374151;
}

.commercial-info .no-commercial {
  color: #9ca3af;
  font-style: italic;
}

.commercial-item {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05));
  border-radius: 8px;
  padding: 0.5rem;
  border-left: 3px solid var(--primary-blue);
}

.commercial-item i {
  color: var(--primary-blue);
}

.commercial-item .no-commercial {
  color: #9ca3af;
  font-style: italic;
}

.commercial-item .commercial-name {
  color: #2e7d32;
  font-weight: 600;
}

.technicien-item {
  background: linear-gradient(135deg, rgba(21, 101, 192, 0.1), rgba(13, 71, 161, 0.05));
  border-radius: 8px;
  padding: 0.5rem;
  border-left: 3px solid #1565c0;
}

.technicien-item i {
  color: #1565c0;
}

.technicien-item .technicien-name {
  color: #1565c0;
  font-weight: 600;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.badge-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.badge-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.badge-info {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.badge-secondary {
  background: linear-gradient(135deg, #6b7280, #4b5563);
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Styles spécifiques renforcés pour tous les badges de statut */
.badge-success,
.badge.badge-success {
  background: linear-gradient(135deg, #10b981, #059669) !important;
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
  border: 1px solid rgba(16, 185, 129, 0.3);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

.badge-warning,
.badge.badge-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706) !important;
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3) !important;
  border: 1px solid rgba(245, 158, 11, 0.3);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
}

.badge-danger,
.badge.badge-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626) !important;
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
  border: 1px solid rgba(239, 68, 68, 0.3);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
}

.badge-reported,
.badge.badge-reported {
  background: linear-gradient(135deg, #a855f7, #9333ea) !important;
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
  border: 1px solid rgba(168, 85, 247, 0.3);
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.2);
}

.badge-info,
.badge.badge-info {
  background: linear-gradient(135deg, #3b82f6, #2563eb) !important;
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
  border: 1px solid rgba(59, 130, 246, 0.3);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
}

.badge-secondary,
.badge.badge-secondary {
  background: linear-gradient(135deg, #6b7280, #4b5563) !important;
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
  border: 1px solid rgba(107, 114, 128, 0.3);
  box-shadow: 0 2px 8px rgba(107, 114, 128, 0.2);
}

/* Badge Terminé/Terminé - Vert vif */
.badge-completed,
.badge.badge-completed {
  background: linear-gradient(135deg, #22c55e, #16a34a) !important;
  color: white !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important;
  border: 1px solid rgba(34, 197, 94, 0.3);
  box-shadow: 0 2px 10px rgba(34, 197, 94, 0.35);
  animation: pulse-green 2s ease-in-out infinite;
}

@keyframes pulse-green {
  0%, 100% { 
    box-shadow: 0 2px 10px rgba(34, 197, 94, 0.35);
  }
  50% { 
    box-shadow: 0 4px 16px rgba(34, 197, 94, 0.5);
  }
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1), rgba(0, 70, 255, 0.05));
  border: 1px solid rgba(0, 27, 183, 0.2);
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--primary-blue);
}

/* Animations */
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

@keyframes bounceIn {
  0% { opacity: 0; transform: scale(0.3); }
  50% { opacity: 1; transform: scale(1.05); }
  70% { transform: scale(0.9); }
  100% { opacity: 1; transform: scale(1); }
}

/* Responsive */
@media (max-width: 768px) {
  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-wrapper {
    min-width: auto;
    width: 100%;
  }
  
  .reset-btn {
    width: 100%;
    margin-top: 0.5rem;
  }
}

/* Boutons de vue */
.view-toggle {
  display: flex;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 4px;
  margin-right: 1rem;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

.view-btn {
  padding: 0.5rem 0.75rem;
  border: none;
  background: transparent;
  color: #64748b;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.9rem;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-btn:hover {
  background: rgba(0, 27, 183, 0.1);
  color: var(--primary-blue);
  transform: translateY(-1px);
}

.view-btn.active {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.3);
  transform: translateY(-2px);
}

/* Vue cartes */
.cards-view {
  margin-top: 1rem;
}

.prospects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  padding: 1rem 0;
}

.prospect-card {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.prospect-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-blue), var(--secondary-blue));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.prospect-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 27, 183, 0.15);
  border-color: rgba(0, 27, 183, 0.2);
}

.prospect-card:hover::before {
  opacity: 1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.client-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.client-name i {
  color: var(--primary-blue);
  font-size: 1.1rem;
}

.client-name strong {
  font-size: 1.1rem;
  color: #1e293b;
  font-weight: 700;
}

.card-body {
  margin-bottom: 1rem;
}

.contact-info {
  margin-bottom: 1rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #64748b;
}

.contact-item i {
  width: 16px;
  color: var(--primary-blue);
  font-size: 0.85rem;
}

.intervention-info {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.85rem;
}

.info-item i {
  color: var(--primary-blue);
}

.description {
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(0, 27, 183, 0.05);
  border-radius: 8px;
  border-left: 3px solid var(--primary-blue);
}

.description p {
  margin: 0;
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 27, 183, 0.1);
}

.creation-date {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #64748b;
}

.creation-date i {
  color: var(--primary-blue);
}

.select-btn {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.select-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.3);
}

/* Boutons d'action */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.action-buttons .btn {
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.action-buttons .btn-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.action-buttons .btn-warning:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.action-buttons .btn-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.action-buttons .btn-danger:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.action-buttons .btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.action-buttons .btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.action-buttons .btn-primary {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.action-buttons .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.3);
}

/* Actions dans le tableau */
.table-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.table-actions .btn {
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.table-actions .btn-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.table-actions .btn-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.table-actions .btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.table-actions .btn-primary {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.table-actions .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Responsive pour les cartes */
@media (max-width: 768px) {
  .prospects-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .prospect-card {
    padding: 1rem;
  }
  
  .card-header {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .intervention-info {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .card-footer {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
  
  .select-btn {
    width: 100%;
    justify-content: center;
  }
  
  .action-buttons {
    width: 100%;
    flex-wrap: wrap;
  }
  
  .action-buttons .btn {
    flex: 1;
    min-width: 80px;
    justify-content: center;
  }
  
  .table-actions {
    flex-direction: column;
    gap: 0.25rem;
  }
}

/* Styles pour les colonnes de catégories */
.category-column {
  min-width: 100px;
  text-align: center;
}

.category-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.category-emoji {
  font-size: 1.5rem;
}

.category-name {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.category-cell {
  text-align: center;
  padding: 0.75rem !important;
}

.category-qty-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0.25rem 0.5rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.95rem;
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.2);
}

.no-qty {
  color: #ccc;
  font-size: 1.2rem;
  font-weight: 300;
}

.date-rdv {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #059669;
  font-size: 0.85rem;
  font-weight: 500;
}

.no-rdv {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #dc2626;
  font-size: 0.85rem;
  font-style: italic;
}

.lieu {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #7c3aed;
  font-size: 0.85rem;
  font-weight: 500;
}

.no-lieu {
  color: #9ca3af;
  font-style: italic;
}

.prix {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #059669;
  font-size: 0.85rem;
  font-weight: 600;
}

.no-prix {
  color: #9ca3af;
  font-style: italic;
}

/* Responsive pour les nouvelles colonnes */
@media (max-width: 1200px) {
  .prospects-table th:nth-child(5),
  .prospects-table td:nth-child(5),
  .prospects-table th:nth-child(6),
  .prospects-table td:nth-child(6) {
    display: none;
  }
}

@media (max-width: 992px) {
  .prospects-table th:nth-child(4),
  .prospects-table td:nth-child(4),
  .prospects-table th:nth-child(7),
  .prospects-table td:nth-child(7) {
    display: none;
  }
}

/* ========== RESPONSIVE MOBILE - FORCER LES CARTES ========== */
@media (max-width: 768px) {
  /* Cacher le toggle de vue sur mobile */
  .view-toggle {
    display: none !important;
  }
  
  /* Cacher le tableau sur mobile */
  .table-view {
    display: none !important;
  }
  
  /* Forcer l'affichage des cartes */
  .cards-view {
    display: block !important;
  }
  
  .filters-section {
    padding: 0;
  }
  
  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-wrapper {
    min-width: auto;
    width: 100%;
  }
  
  .search-input {
    max-width: none;
  }
  
  .reset-btn {
    width: 100%;
    justify-content: center;
  }
  
  .prospects-manager {
    padding: 0.5rem;
  }
  
  .prospects-content-container {
    margin-top: 1rem;
  }
}

@media (max-width: 480px) {
  .prospect-card {
    padding: 0.875rem;
    border-radius: 12px;
  }
  
  .client-name strong {
    font-size: 1rem;
  }
  
  .contact-item {
    font-size: 0.85rem;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .action-buttons .btn {
    width: 100%;
  }
}

/* ============================================
   PAGINATION STYLES
   ============================================ */
.pagination-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.pagination-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.pagination-text {
  font-size: 0.9rem;
  color: #64748b;
}

.pagination-text strong {
  color: var(--primary-blue);
  font-weight: 600;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #64748b;
}

.per-page-select {
  padding: 0.4rem 0.75rem;
  border: 1px solid rgba(0, 27, 183, 0.2);
  border-radius: 8px;
  background: white;
  color: #374151;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.per-page-select:hover {
  border-color: var(--primary-blue);
}

.per-page-select:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0.5rem;
  border: 1px solid rgba(0, 27, 183, 0.2);
  border-radius: 10px;
  background: white;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.pagination-btn:hover:not(:disabled):not(.active) {
  background: rgba(0, 27, 183, 0.05);
  border-color: var(--primary-blue);
  color: var(--primary-blue);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.15);
}

.pagination-btn.active {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-color: var(--primary-blue);
  color: white;
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.3);
  transform: translateY(-2px);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f1f5f9;
}

.pagination-nav {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.02));
}

.pagination-nav:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1), rgba(0, 70, 255, 0.05));
}

.pagination-ellipsis {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  color: #9ca3af;
  font-weight: 600;
  letter-spacing: 2px;
}

/* Responsive pagination */
@media (max-width: 768px) {
  .pagination-container {
    padding: 1rem;
  }
  
  .pagination-info {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .pagination-controls {
    gap: 0.35rem;
  }
  
  .pagination-btn {
    min-width: 36px;
    height: 36px;
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .pagination-btn {
    min-width: 32px;
    height: 32px;
    font-size: 0.8rem;
    border-radius: 8px;
  }
  
  .items-per-page {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
